/**
 * Notification setup banner coordination with onboarding auto-prompts.
 *
 * Post-registration order (F5): destination complete → homepage tour → near-me
 * modal (if off) → setup banner. Use session helpers below to avoid stacked
 * “finish setup” prompts.
 *
 * | Surface | Priority | Blocks setup banner? |
 * | -------------------------- | -------- | -------------------- |
 * | Homepage tour (modal) | 100 | Yes |
 * | PWA post-install push | 98 | Yes |
 * | Homepage near-me modal | 97 | Yes |
 * | Post-registration grace | 96 | Yes (banner strip) |
 * | Notification setup banner | 95 | N/A (app-shell strip)|
 * | Sell / my-items intros | 80 / 70 | No |
 * | Market local banner slot | 60 | No — max one of welcome / push reminder; welcome 3rd bullet links to alert settings when near-me enabled |
 */
export const NOTIFICATION_SETUP_BANNER_ONBOARDING_PRIORITY = 95;

const POST_REGISTRATION_GRACE_KEY = "sell-it-off:post-registration-setup-grace";
/** Brief window after onboarding destination so tour / near-me modal run first. */
const POST_REGISTRATION_GRACE_MS = 45 * 60 * 1000;

export type NotificationPromptSuppressionContext = {
  activeOnboardingPromptId: string | null;
  nearMeModalOpen?: boolean;
  pwaPushPending?: boolean;
  postRegistrationGrace?: boolean;
};

/** True when an onboarding-engine modal prompt is active (tour, etc.). */
export function shouldDeferNotificationSetupSheet(
  activeOnboardingPromptId: string | null,
): boolean {
  return activeOnboardingPromptId != null;
}

export function markPostRegistrationSetupGrace(): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(POST_REGISTRATION_GRACE_KEY, String(Date.now()));
}

export function clearPostRegistrationSetupGrace(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(POST_REGISTRATION_GRACE_KEY);
}

export function readPostRegistrationSetupGraceActive(): boolean {
  if (typeof window === "undefined") return false;
  const raw = sessionStorage.getItem(POST_REGISTRATION_GRACE_KEY);
  if (!raw) return false;
  const started = Number(raw);
  if (!Number.isFinite(started)) {
    sessionStorage.removeItem(POST_REGISTRATION_GRACE_KEY);
    return false;
  }
  if (Date.now() - started > POST_REGISTRATION_GRACE_MS) {
    sessionStorage.removeItem(POST_REGISTRATION_GRACE_KEY);
    return false;
  }
  return true;
}

/** Hide non-modal setup banner strip while higher-priority prompts may show. */
export function shouldSuppressNotificationSetupBanner(
  context: NotificationPromptSuppressionContext,
): boolean {
  if (shouldDeferNotificationSetupSheet(context.activeOnboardingPromptId)) {
    return true;
  }
  if (context.nearMeModalOpen) {
    return true;
  }
  if (context.pwaPushPending) {
    return true;
  }
  if (context.postRegistrationGrace ?? readPostRegistrationSetupGraceActive()) {
    return true;
  }
  return false;
}

/** Call when near-me modal closes or tour completes so banner can appear. */
export function maybeClearPostRegistrationSetupGrace(): void {
  if (readPostRegistrationSetupGraceActive()) {
    clearPostRegistrationSetupGrace();
  }
}
